import React, { useState } from "react";
import Sheet from "@amzn/meridian/sheet";
import Row from "@amzn/meridian/row";
import Column from "@amzn/meridian/column";
import Button from "@amzn/meridian/button";
import Box from "@amzn/meridian/box";
import Text from "@amzn/meridian/text";
import Input from "@amzn/meridian/input";
import _ from "lodash";
import Icon from "@amzn/meridian/icon";
import closeMediumTokens from "@amzn/meridian-tokens/base/icon/close-medium";
import { SheetSide, SheetType } from "@amzn/meridian/sheet/sheet";
import { ColumnWidth } from "@amzn/meridian/column/column";
import { BoxHeight } from "@amzn/meridian/box/box";
import { RowHeight } from "@amzn/meridian/row/row";

const gridConfig = [
    {
        columns: [
            { label: "Lease Name", path: "user.leaseName" },
            { label: "Lease Number", path: "user.leaseNumber" },
            { label: "Lease ID", path: "user.leaseId" },
        ],
    },
    {
        columns: [
            { label: "Location Code", path: "user.locationCode" },
            { label: "Airport Code", path: "user.airportCode" },
            { label: "Brand Name", path: "user.brandName" },
        ],
    },
    {
        columns: [
            { label: "Address", path: "user.address" },
            { label: "City", path: "user.city" },
            { label: "State", path: "user.stateProvince" },
        ],
    },
    {
        columns: [
            { label: "ZIP", path: "user.zip" },
            { label: "Country", path: "user.country" },
            { label: "Continent", path: "user.continent" },
        ],
    },
    {
        columns: [
            { label: "Ownership Type", path: "user.ownershipType" },
            { label: "Lease Type Code", path: "user.leaseTypeCode" },
            { label: "Lease Status", path: "user.leaseStatus" },
        ],
    },
    {
        columns: [
            { label: "Legal Entity", path: "user.legalEntity" },
            { label: "Amazon Legal Entity", path: "user.amazonLegalEntity" },
            { label: "Lease Portfolio Type", path: "user.leasePortfolioType" },
        ],
    },
    {
        columns: [
            { label: "Current Term Begin", path: "user.currentTermBegin" },
            { label: "Current Term End", path: "user.currentTermEnd" },
            { label: "Rent Commencement", path: "user.rentCommencement" },
        ],
    },
    {
        columns: [
            { label: "Original Term Begin", path: "user.originalTermBegin" },
            { label: "Original Term End", path: "user.originalTermEnd" },
            { label: "Rent End", path: "user.rentEnd" },
        ],
    },
    {
        columns: [
            { label: "Rentable Area", path: "user.rentableArea" },
            { label: "Unit Rentable Area", path: "user.unitRentableArea" },
            { label: "Total Area GLA", path: "user.totalAreaGla" },
        ],
    },
    {
        columns: [
            { label: "Annual Rent", path: "user.annualRent" },
            { label: "Currency", path: "user.currency" },
            { label: "Prorate Share", path: "user.prorateShare" },
        ],
    },
    {
        columns: [
            { label: "Facility Type", path: "user.facilityType" },
            { label: "Facility Description", path: "user.facilityDescription" },
            { label: "Property Type", path: "user.propertyType" },
        ],
    },
    {
        columns: [
            { label: "Cost Center", path: "user.costCenter" },
            { label: "Company Code 1", path: "user.companyCode1" },
            { label: "Company Code 2", path: "user.companyCode2" },
        ],
    },
    {
        columns: [
            { label: "Lease Supplier 1", path: "user.leaseSupplier1" },
            { label: "Lease Supplier 2", path: "user.leaseSupplier2" },
            { label: "Contact Name", path: "user.contactName" },
        ],
    },
    {
        columns: [
            { label: "Lease Financial Group", path: "user.leaseFinancialGroup" },
            { label: "Status", path: "user.status" },
            { label: "Status Description", path: "user.statusDescription" },
        ],
    },
];

export const TangoDataSheet: React.FC<{
    data: any;
    open: boolean;
    setOpen: (open: boolean) => void;
    sheetType?: SheetType;
    sheetSide?: SheetSide;
    columnWidth?: ColumnWidth;
    boxHeight?: BoxHeight;
    rowHeight?: RowHeight;
}> = ({
    data,
    open,
    setOpen,
    sheetType = "overlay",
    sheetSide = "bottom",
    columnWidth = "100%",
    boxHeight = 400,
    rowHeight = "40px",
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editedData, setEditedData] = useState(data);

    const onClose = () => {
        setOpen(false);
        setIsEditing(false);
        setEditedData(data);
    };

    const handleCancel = () => {
        setIsEditing(false);
        setEditedData(data);
        setOpen(false);
    };

    const handleInputChange = (path: string, value: string) => {
        setEditedData(prevData =>
            _.set({ ...prevData }, `propertyDetails.tangoData.${path}`, value)
        );
    };

    return (
        <Sheet type={sheetType} side={sheetSide} open={open} onClose={onClose}>
            <Column heights={["fill", "fit"]} width={columnWidth} height="100%">
                <Row width="100%" alignmentHorizontal="end">
                    <>
                        <Button onClick={handleCancel} type={"icon"}>
                            <Icon tokens={closeMediumTokens}></Icon>
                        </Button>
                    </>
                </Row>
                <Box spacingInset="100 100 100 500">
                    <Text type={"h200"}>Tango Data</Text>
                </Box>
                <Box spacingInset="100 100 100 600" overflowY={"scroll"} maxHeight={boxHeight}>
                    {gridConfig.map((row, rowIndex) => (
                        <React.Fragment key={rowIndex}>
                            <Row
                                width="100%"
                                widths={row.columns.map(() => `${100 / row.columns.length}%`)}
                            >
                                {row.columns.map(({ label }, columnIndex) => (
                                    <Text key={columnIndex} color="secondary" type="b300">
                                        {label}
                                    </Text>
                                ))}
                            </Row>
                            <Row
                                width="100%"
                                height={rowHeight}
                                alignmentVertical="top"
                                widths={row.columns.map(() => `${100 / row.columns.length}%`)}
                            >
                                {row.columns.map(({ path }, columnIndex) => (
                                    <Box key={columnIndex} spacingInset="100 none none none">
                                        {isEditing ? (
                                            <Input
                                                value={_.get(
                                                    editedData,
                                                    `propertyDetails.tangoData.${path}`,
                                                    ""
                                                )}
                                                onChange={e => handleInputChange(path, e)}
                                            />
                                        ) : (
                                            <Text color="primary" type="h100">
                                                {_.get(
                                                    data,
                                                    `propertyDetails.tangoData.${path}`,
                                                    ""
                                                )}
                                            </Text>
                                        )}
                                    </Box>
                                ))}
                            </Row>
                        </React.Fragment>
                    ))}
                </Box>
            </Column>
        </Sheet>
    );
};
